import type { Marker } from "@repo/ui";

import { dedupeByCoordinates, filterPositionsWithinRadius, filterPositionsWithinSquare, MapChart } from "@repo/ui";
import { useMemo, useRef } from "react";

import type { LayerDefinition, PositionViewModel } from "../../models";
import type { GeoFilter } from "./filters/filterContext";
import type { LayerResults } from "./hooks/layerQueries";

import { useMapConfig } from "./config/mapConfigContext";
import { geoFilterArea, useMapFilters } from "./filters/filterContext";
import {
	buildPathMarkers,
	buildPinMarkers,
	pathMarkersSignature,
	pinMarkersSignature,
	showsPaths,
} from "./layerMarkers";
import { isLayerVisible } from "./layers/layer";
import { useLayerResults, useMapLayers } from "./layers/layerContext";

export function useLayeredMapData() {
	const { layers } = useMapLayers();
	const results = useLayerResults();
	const { isTransitioning } = useMapConfig();
	const { filters } = useMapFilters();
	const { poi, distanceShape, distanceMiles } = filters;

	const pinMarkers = useCachedLayerMarkers(
		layers.data,
		results,
		isLayerVisible,
		buildPinMarkers,
		pinMarkersSignature,
	);
	const pathMarkers = useCachedLayerMarkers(layers.data, results, showsPaths, buildPathMarkers, pathMarkersSignature);

	const dedupedPins = useMemo(() => dedupeByCoordinates(pinMarkers), [pinMarkers]);
	const markers = useMemo(
		() => filterByPoi(dedupedPins, { poi, distanceShape, distanceMiles }),
		[dedupedPins, poi, distanceShape, distanceMiles],
	);
	// Path points are not deduped by coordinate, which would drop points from one rep's path when another
	// layer pins the same spot.
	const lineMarkers = useMemo(
		() => filterByPoi(pathMarkers, { poi, distanceShape, distanceMiles }),
		[pathMarkers, poi, distanceShape, distanceMiles],
	);

	const loading = isTransitioning || layers.data.some((layer) => results.get(layer.id)?.isLoading);
	const notice = loading ? undefined : mapNotice(layers.data, results, markers.length);

	return { markers, lineMarkers, loading, notice, center: poi, geoMiles: geoFilterArea(filters)?.miles };
}

export function LayeredMap({ markers, lineMarkers, loading, notice, center }: ReturnType<typeof useLayeredMapData>) {
	return (
		<section className="relative h-full">
			<MapChart
				data={markers}
				type="map"
				mapType="roadmap"
				id="layeredMap"
				height="100%"
				gestureHandling="greedy"
				loading={loading}
				forcedCenter={center}
				lineData={lineMarkers}
				showLines={lineMarkers.length > 0}
			/>
			{notice && (
				<div className="pointer-events-none absolute inset-x-14 bottom-8 z-10 flex justify-center lg:inset-x-0 lg:bottom-auto lg:top-4">
					<div className="pointer-events-auto rounded-md border border-border bg-background/95 px-4 py-2 text-center text-sm font-medium text-muted-foreground shadow-md">
						{notice}
					</div>
				</div>
			)}
		</section>
	);
}

function mapNotice(layers: LayerDefinition[], results: LayerResults, markerCount: number) {
	if (layers.some((layer) => results.get(layer.id)?.isError)) return "Some layers failed to load.";
	if (layers.length > 0 && markerCount === 0) return "No pins to display. Try adjusting your filters.";
	return undefined;
}

interface CachedLayerMarkers {
	signature: string;
	items: PositionViewModel[];
	markers: Marker[];
}

function useCachedLayerMarkers(
	layers: LayerDefinition[],
	results: LayerResults,
	include: (layer: LayerDefinition) => boolean,
	build: (layer: LayerDefinition, items: PositionViewModel[]) => Marker[],
	signature: (layer: LayerDefinition) => string,
) {
	const cache = useRef(new Map<string, CachedLayerMarkers>());
	const combined = useRef<{ perLayer: Marker[][]; markers: Marker[] }>({ perLayer: [], markers: [] });

	return useMemo(() => {
		const nextCache = new Map<string, CachedLayerMarkers>();
		const perLayer = layers.filter(include).map((layer) => {
			const items = (results.get(layer.id)?.items ?? []) as PositionViewModel[];
			const layerSignature = signature(layer);
			const cached = cache.current.get(layer.id);
			const markers =
				cached?.signature === layerSignature && sameElements(cached.items, items)
					? cached.markers
					: build(layer, items);
			nextCache.set(layer.id, { signature: layerSignature, items, markers });
			return markers;
		});
		cache.current = nextCache;

		if (!sameElements(combined.current.perLayer, perLayer)) {
			combined.current = { perLayer, markers: perLayer.flat() };
		}
		return combined.current.markers;
	}, [layers, results, include, build, signature]);
}

function sameElements<T>(a: readonly T[], b: readonly T[]) {
	return a === b || (a.length === b.length && a.every((item, index) => item === b[index]));
}

function filterByPoi<T extends Marker>(markers: T[], filter: GeoFilter) {
	const area = geoFilterArea(filter);
	if (!area) return markers;

	const { poi, miles, shape } = area;
	return shape === "circle"
		? filterPositionsWithinRadius(poi.lat, poi.lng, markers, miles)
		: filterPositionsWithinSquare(poi.lat, poi.lng, markers, miles);
}
